export const FREE_JOB_PACK_OFFER = "free_first_job";
export const FREE_JOB_PACK_CTA_TEXT = "Create my free job pack";

// Shared-document acquisition CTA (SB-0194 / report #765). Recipients who open
// a shared compliance document — either a full-project pack at /shared/[token]
// or a single doc at /share/[token] — are a captive, high-intent audience: this
// routes them to the free-first-job funnel with UTM attribution. The copy is
// the brand-reviewed wording shipped to the pack portal in PR #772.
export const SHARED_PACK_CTA_TEXT = "Create your own job pack — free";
export const SHARED_PACK_FUNNEL_PATH = "/first-job-free";

/**
 * Build the free-first-job funnel href for a shared-document footer CTA.
 *
 * `utmContent` distinguishes the originating surface so the two footers can be
 * told apart in analytics (defaults to the pack portal's value so the existing
 * /shared/[token] call site is unchanged). The single-doc /share/[token] footer
 * passes `"single_doc_footer"`.
 */
export function sharedPackFunnelHref(
  utmContent: string = "shared_pack_footer",
): string {
  const params = new URLSearchParams({
    utm_source: "shared_pack",
    utm_medium: "referral",
    utm_campaign: FREE_JOB_PACK_OFFER,
    utm_content: utmContent,
  });
  return `${SHARED_PACK_FUNNEL_PATH}?${params.toString()}`;
}

export type FreeJobPackPresetKey =
  | "electrician"
  | "roofer"
  | "builder"
  | "extension"
  | "loft";

export interface FreeJobPackPreset {
  key: FreeJobPackPresetKey;
  title: string;
  label: string;
  ctaLabel: string;
  scopeDescription: string;
  estimatedDuration: string;
  domesticClient: boolean;
  includedDocuments: string[];
  upgradeMoment: string;
}

export const freeJobPackPresets: Record<FreeJobPackPresetKey, FreeJobPackPreset> = {
  electrician: {
    key: "electrician",
    title: "Electrical first fix job pack",
    label: "electrician",
    ctaLabel: "Create my free electrician job pack",
    scopeDescription:
      "Electrical installation job pack. Start by adding the site address, then check the electrical scope, isolation arrangements, cable routes, working-at-height tasks and any occupied-property risks.",
    estimatedDuration: "1-4 weeks",
    domesticClient: true,
    includedDocuments: ["Electrical RAMS", "Construction Phase Plan", "Site induction"],
    upgradeMoment: "Prompt for Pro when the user starts a second electrical job or needs branded PDFs.",
  },
  roofer: {
    key: "roofer",
    title: "Roofing job pack",
    label: "roofer",
    ctaLabel: "Create my free roofing job pack",
    scopeDescription:
      "Roofing job pack. Start by adding the site address, then check roof type, edge protection, scaffold access, fragile surfaces, hot works, weather controls and rescue arrangements.",
    estimatedDuration: "1-4 weeks",
    domesticClient: true,
    includedDocuments: ["Roofing RAMS", "Construction Phase Plan", "Site induction"],
    upgradeMoment: "Prompt for Pro when the user needs branded RAMS, COSHH or a second roofing project.",
  },
  builder: {
    key: "builder",
    title: "General building job pack",
    label: "builder",
    ctaLabel: "Create my free builder job pack",
    scopeDescription:
      "General building job pack. Start by adding the site address, then check the project scope, subcontractors, structural work, temporary works, welfare, site access and emergency arrangements.",
    estimatedDuration: "1-3 months",
    domesticClient: true,
    includedDocuments: ["Builder RAMS", "Construction Phase Plan", "Site induction"],
    upgradeMoment: "Prompt for Pro when the user adds job two, team records or branded document packs.",
  },
  extension: {
    key: "extension",
    title: "House extension job pack",
    label: "extension",
    ctaLabel: "Create my free extension job pack",
    scopeDescription:
      "House extension job pack. Start by adding the site address, then check excavation, structural openings, scaffold or roof works, drainage, services, waste, welfare and occupant protection.",
    estimatedDuration: "1-3 months",
    domesticClient: true,
    includedDocuments: ["Extension RAMS", "Construction Phase Plan", "Site induction"],
    upgradeMoment: "Prompt for Pro when the user starts another extension or needs client-ready branding.",
  },
  loft: {
    key: "loft",
    title: "Loft conversion job pack",
    label: "loft conversion",
    ctaLabel: "Create my free loft job pack",
    scopeDescription:
      "Loft conversion job pack. Start by adding the site address, then check scaffold access, stair opening protection, structural steel, asbestos risk, hot works, fire controls and temporary works.",
    estimatedDuration: "1-3 months",
    domesticClient: true,
    includedDocuments: ["Loft conversion RAMS", "Construction Phase Plan", "Site induction"],
    upgradeMoment: "Prompt for Pro when the user needs job two, branded PDFs or site records beyond the first project.",
  },
};

const presetByTradeSlug: Record<string, FreeJobPackPresetKey> = {
  electricians: "electrician",
  roofers: "roofer",
  builders: "builder",
};

export function getFreeJobPackPresetForTradeSlug(
  tradeSlug: string,
): FreeJobPackPreset | null {
  const presetKey = presetByTradeSlug[tradeSlug];
  return presetKey ? freeJobPackPresets[presetKey] : null;
}

export function freeJobPackHref({
  utmContent,
  presetKey,
}: {
  utmContent: string;
  presetKey?: FreeJobPackPresetKey | null;
}): string {
  const params = new URLSearchParams({
    utm_offer: FREE_JOB_PACK_OFFER,
    utm_content: utmContent,
  });
  if (presetKey) params.set("job_preset", presetKey);
  return `/start-project?${params.toString()}`;
}

/**
 * The free-first-job activation funnel: landing → signup → first job → first
 * document → paid. Each entry is a funnel STEP whose `posthogEvent` names the
 * concrete PostHog event that actually backs it in production, with an optional
 * `posthogFilter` when the step is a property-filtered slice of a shared event.
 *
 * Two steps deliberately reuse the existing `document_downloaded` event rather
 * than emitting near-duplicate names: the in-app PDF preview fires it with
 * `preview: true` (viewing) and the true download fires it with `preview: false`
 * (delivery) — see `app/api/documents/[documentId]/download/route.ts`. The
 * remaining steps emit an event whose name matches the step key.
 *
 * Keep `posthogEvent` in lockstep with the real emit sites — the
 * `free-first-job-funnel-instrumentation` test asserts every one of these is
 * genuinely captured somewhere in the codebase, so a step can never silently
 * drift back to being declared-but-never-fired (the original bug: 7 of the 8
 * steps were never instrumented, and signup was declared as `signup_started`
 * while the live tracker fired `sign_up_start`).
 */
export const freeFirstJobMeasurementEvents = [
  {
    event: "job_description_entered",
    posthogEvent: "job_description_entered",
    trigger: "Visitor saves the scope or accepts a preset in the draft-project flow.",
    purpose: "Confirms the free-first-job promise produced a usable job brief.",
  },
  {
    // Naming decided 2026-07-19 (issue #1385): `signup_started` is the
    // contract name and the tracker's PostHog capture now fires it. Only the
    // GA4/gtag event keeps the legacy `sign_up_start` (Google Ads conversion
    // mappings key on it). Historic PostHog `sign_up_start` data stays
    // queryable under the old name.
    event: "signup_started",
    posthogEvent: "signup_started",
    trigger: "Visitor reaches account creation with an unclaimed draft session.",
    purpose: "Measures handoff from anonymous job demo to account creation.",
  },
  {
    event: "first_project_generated",
    posthogEvent: "first_project_generated",
    trigger: "The draft is claimed and the first project workspace is created.",
    purpose: "Activation point for the free first job pack.",
  },
  {
    event: "document_previewed",
    posthogEvent: "document_downloaded",
    posthogFilter: { preview: true },
    trigger: "User opens the generated RAMS, CPP or induction preview.",
    purpose: "Measures whether the draft output is inspected before download or share.",
  },
  {
    event: "pdf_downloaded_or_shared",
    posthogEvent: "document_downloaded",
    posthogFilter: { preview: false },
    trigger: "User downloads a PDF or creates a share link from the first project.",
    purpose: "Measures the first value moment without assuming compliance acceptance.",
  },
  {
    event: "branding_attempted",
    posthogEvent: "branding_attempted",
    trigger: "Starter user tries to add logo, remove watermark or use branded output.",
    purpose: "Identifies brand-led upgrade demand.",
  },
  {
    event: "second_job_started",
    posthogEvent: "second_job_started",
    trigger: "Starter user attempts to create another project after the free first job.",
    purpose: "Primary product-led paid conversion point.",
  },
  {
    event: "upgrade_clicked",
    posthogEvent: "upgrade_clicked",
    trigger: "User clicks a Pro or setup-service upgrade CTA from a gated moment.",
    purpose: "Connects upgrade intent to the exact product need.",
  },
] as const;

export const freeFirstJobUpgradeTriggers = [
  {
    trigger: "Second job start",
    copy: "You have used the free first job. Upgrade to Pro to run the next project.",
  },
  {
    trigger: "Branded output attempt",
    copy: "Add your company logo and remove the Starter watermark with Pro.",
  },
  {
    trigger: "PDF download or share after review",
    copy: "Review the draft first. Upgrade only when you need branded packs or ongoing work.",
  },
  {
    trigger: "COSHH, worker certs or site records",
    copy: "These are Pro features for teams running work repeatedly, not blockers for the first free pack.",
  },
] as const;
